import { useSlideScreenshotQueue } from "@/components/slides/screenshot/SlideScreenshotQueue";
import type { LessonDataSourceEnum } from "@/generated/graphql";
import type { SortableHandleProps } from "@/lib/dnd/components/Sortable";
import { Sortable } from "@/lib/dnd/components/Sortable";
import { useSectionCosts } from "@/lib/hooks/aiUsage/useAiUsage";
import type { GenerationPreset } from "@/lib/hooks/lessonGenerator/useGenerationPresets";
import {
  useConfirmLessonGeneration,
  useGenerationRun,
  useRegenerateLessonSection,
  useSaveLessonGenerationDraft,
} from "@/lib/hooks/lessonGenerator/useLessonGeneration";
import { useTags } from "@/lib/hooks/tags/useTags";
import { getTemplateLabel } from "@/lib/slides/slideTemplates";
import { formatTwd } from "@/lib/utils/aiCost";
import { describeAiError } from "@/lib/utils/aiCredits";
import { Button, LoadingSpinner, Modal } from "@ai-teach/ui-web";
import { verticalListSortingStrategy } from "@dnd-kit/sortable";
import { useEffect, useState } from "react";
import { HiOutlinePencil, HiOutlineTrash, HiPlus } from "react-icons/hi";
import { RxDragHandleDots2 } from "react-icons/rx";
import {
  BookLessonPicker,
  type BookLessonSelection,
  EMPTY_BOOK_SELECTION,
  useBookLessonRef,
} from "./BookLessonPicker";
import { DraftSheet } from "./DraftSheet";
import { PresetBar } from "./PresetBar";
import { ReferenceSlideThumbnail } from "./ReferenceSlideThumbnail";
import { SectionEditorModal } from "./SectionEditorModal";
import { type PickableTag, TagPill } from "./TagPill";
import {
  DATA_SOURCES,
  describeCount,
  describeParent,
  resolveSlideCount,
  toCountInput,
  writtenLines,
} from "./countRules";
import {
  type DraftSlide,
  type EditableField,
  groupChecks,
  parseDraft,
  replaceFields,
  setDeleted,
  setSectionDeleted,
} from "./draftFields";
import { readsNothing } from "./types";
import type { BookLessonRef, GeneratorSection } from "./types";

const AUTOSAVE_DELAY_MS = 1000;
const REFERENCE_THUMBNAIL_WIDTH = 56;

const toSlideInput = (slide: DraftSlide) => ({
  id: slide.id,
  sectionId: slide.sectionId,
  name: slide.name,
  deleted: slide.deleted,
  fields: slide.fields,
});

function Step({
  number,
  title,
  hint,
  children,
}: {
  number: number;
  title: string;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <section className="space-y-2.5">
      <div className="flex items-baseline gap-2.5">
        <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-primary-500/10 text-[11px] font-bold text-primary-500">
          {number}
        </span>
        <h3 className="card-title">{title}</h3>
      </div>
      {hint && <p className="pl-8 text-xs text-gray-500 dark:text-gray-400">{hint}</p>}
      <div className="pl-8">{children}</div>
    </section>
  );
}

function SectionRow({
  section,
  index,
  tags,
  targetBook,
  dragHandle,
  onEdit,
  onRemove,
}: {
  section: GeneratorSection;
  index: number;
  tags: PickableTag[];
  targetBook: BookLessonRef | null;
  dragHandle?: SortableHandleProps | undefined;
  onEdit: () => void;
  onRemove: () => void;
}) {
  const resolved = section.count
    ? resolveSlideCount(section.count, targetBook, section.contextLines)
    : null;
  const sources = section.context.map((one) => DATA_SOURCES[one].label).join(" · ");
  const parentTag = section.parent
    ? (tags.find((one) => one.id === section.parent?.tagId)?.name ?? section.parent.tagId)
    : "";

  return (
    <div className="rounded-xl border border-gray-200 bg-background transition-colors hover:border-gray-300 dark:border-gray-800 dark:hover:border-gray-700">
      <div className="flex items-start gap-3 px-4 py-3">
        {dragHandle && (
          <button
            type="button"
            ref={dragHandle.ref}
            {...dragHandle.attributes}
            {...dragHandle.listeners}
            aria-label="Drag to reorder section"
            className="mt-px shrink-0 cursor-grab text-gray-300 transition-colors hover:text-gray-500 active:cursor-grabbing dark:text-gray-600 dark:hover:text-gray-400"
          >
            <RxDragHandleDots2 className="h-4 w-4" />
          </button>
        )}
        <span className="mt-px flex h-5 w-5 shrink-0 items-center justify-center rounded-md bg-gray-100 text-[11px] font-semibold text-gray-500 dark:bg-white/5 dark:text-gray-400">
          {index + 1}
        </span>

        <ReferenceSlideThumbnail
          slide={section.referenceSlide.slide}
          width={REFERENCE_THUMBNAIL_WIDTH}
        />

        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <h4 className="truncate text-sm font-semibold text-gray-900 dark:text-gray-100">
              {section.name ?? getTemplateLabel(section.template)}
            </h4>
            {resolved !== null && (
              <span className="shrink-0 rounded-full bg-primary-500/10 px-2 py-0.5 text-[11px] font-semibold text-primary-600 dark:text-primary-300">
                {resolved} slide{resolved === 1 ? "" : "s"}
              </span>
            )}
            {section.referenceSlide.tagIds.map((id) => {
              const tag = tags.find((one) => one.id === id);
              return tag ? <TagPill key={id} tag={tag} /> : null;
            })}
          </div>

          <div className="mt-1.5 flex flex-wrap items-center gap-x-1.5 gap-y-0.5 text-[11px] text-gray-500 dark:text-gray-400">
            {section.name !== undefined && (
              <>
                <span className="truncate">{getTemplateLabel(section.template)}</span>
                <span aria-hidden>·</span>
              </>
            )}
            <span className="truncate">
              {section.parent ? describeParent(section.parent, parentTag) : sources || "no context"}
            </span>
            {section.sectionScopes?.length ? (
              <>
                <span aria-hidden>·</span>
                <span className="truncate">in {section.sectionScopes.join(", ")}</span>
              </>
            ) : null}
            {section.count?.rule === "perSourceItem" && (
              <>
                <span aria-hidden>·</span>
                <span className="truncate">{describeCount(section.count)}</span>
              </>
            )}
            <span aria-hidden>·</span>
            <span className="truncate">
              ref <span className="font-mono">{section.referenceSlide.name}</span>
            </span>
          </div>
        </div>

        <div className="flex shrink-0 gap-0.5">
          <button
            type="button"
            onClick={onEdit}
            aria-label="Edit section"
            className="rounded-lg p-1.5 text-gray-400 transition-colors hover:bg-black/5 hover:text-gray-700 dark:hover:bg-white/5 dark:hover:text-gray-200"
          >
            <HiOutlinePencil className="h-4 w-4" />
          </button>
          <button
            type="button"
            onClick={onRemove}
            aria-label="Remove section"
            className="rounded-lg p-1.5 text-gray-400 transition-colors hover:bg-error-500/10 hover:text-error-500"
          >
            <HiOutlineTrash className="h-4 w-4" />
          </button>
        </div>
      </div>

      {section.prompt !== "" && (
        <div className="flex gap-2 border-t border-gray-200 py-2 pl-12 pr-4 dark:border-gray-800">
          <span className="shrink-0 pt-0.5 text-[10px] font-semibold uppercase tracking-wider text-gray-400">
            Prompt
          </span>
          <p className="line-clamp-2 text-xs leading-relaxed text-gray-600 dark:text-gray-300">
            {section.prompt}
          </p>
        </div>
      )}
    </div>
  );
}

export interface GenerateSlidesModalProps {
  isOpen: boolean;
  onClose: () => void;
  lessonId: string;
  /** Opens straight into an existing draft rather than the setup steps. */
  resumeJobId?: string;
  onSlidesAdded: () => void;
}

export function GenerateSlidesModal({
  isOpen,
  onClose,
  lessonId,
  resumeJobId,
  onSlidesAdded,
}: GenerateSlidesModalProps) {
  const [target, setTarget] = useState<BookLessonSelection>(EMPTY_BOOK_SELECTION);
  const [sections, setSections] = useState<GeneratorSection[]>([]);
  const [editing, setEditing] = useState<GeneratorSection | null>(null);
  const [isEditorOpen, setIsEditorOpen] = useState(false);
  const [draft, setDraft] = useState<DraftSlide[] | null>(null);
  const [editedSlideIds, setEditedSlideIds] = useState<ReadonlySet<string>>(new Set());

  const run = useGenerationRun(resumeJobId ?? null);
  const { saveDraft } = useSaveLessonGenerationDraft();
  const { confirm, confirming } = useConfirmLessonGeneration();
  const { regenerateSection } = useRegenerateLessonSection();

  const { tags } = useTags();
  const sectionCosts = useSectionCosts(
    run.jobId,
    run.isRunning || run.job?.regeneratingSectionId != null
  );
  const enqueueScreenshots = useSlideScreenshotQueue();
  const targetBook = useBookLessonRef(target);

  const totalSlides = sections.reduce(
    (sum, section) =>
      sum +
      (section.count
        ? (resolveSlideCount(section.count, targetBook, section.contextLines) ?? 0)
        : 0),
    0
  );
  const hasSubslideSection = sections.some((section) => section.parent);
  const needsBook = sections.some((section) => !section.parent);
  const emptySections = sections.filter(readsNothing);
  const canGenerate = sections.length > 0 && (targetBook !== null || !needsBook);

  const upsertSection = (section: GeneratorSection) => {
    setSections((prev) => {
      const index = prev.findIndex((one) => one.id === section.id);
      if (index === -1) return [...prev, section];
      return prev.map((one) => (one.id === section.id ? section : one));
    });
  };

  // A section's edited lines are that book lesson's own, so they go when another is picked.
  const chooseTarget = (next: BookLessonSelection) => {
    setTarget(next);
    setSections((prev) => prev.map(({ contextLines: _lines, partLines: _parts, ...rest }) => rest));
  };

  const applyPreset = (preset: GenerationPreset) => {
    setTarget((prev) => ({
      ...preset.target,
      lesson: prev.seriesSlug === preset.target.seriesSlug ? prev.lesson : null,
    }));
    setSections(preset.sections);
  };

  const generate = async () => {
    if (!canGenerate) return;
    setDraft(null);
    await run.start({
      lessonId,
      bookLessonId: targetBook?.id ?? null,
      sections: sections.map((section) => ({
        id: section.id,
        template: section.template,
        referenceSlideId: section.referenceSlide.id,
        context: section.context as LessonDataSourceEnum[],
        sectionScopes: section.sectionScopes ?? [],
        subLists: section.subLists ?? [],
        contextLines: section.parent
          ? []
          : writtenLines(section.contextLines ?? []).map((edit) => ({
              source: edit.source as LessonDataSourceEnum,
              lines: edit.lines,
            })),
        partLines: section.parent ? [] : (section.partLines ?? []),
        count: section.count ? toCountInput(section.count) : null,
        parent: section.parent ?? null,
        prompt: section.prompt,
        randomizeChoicePositions: section.randomizeChoicePositions ?? false,
        generateSentence: section.generateSentence ?? false,
        slideName: section.slideName,
        numberSlides: section.numberSlides,
      })),
    });
  };

  const draftSlides = draft ?? (run.job?.status === "done" ? parseDraft(run.job.draft) : null);

  // An edited slide's flags go with the wording they were about, before the save that drops them.
  const editDraftFields = (slideIndex: number, fields: EditableField[]) => {
    if (!draftSlides) return;
    const slideId = draftSlides[slideIndex]?.id;
    if (slideId) setEditedSlideIds((prev) => new Set(prev).add(slideId));
    setDraft(replaceFields(draftSlides, slideIndex, fields));
  };

  const dropDraftSlide = (slideIndex: number, deleted: boolean) => {
    if (draftSlides) setDraft(setDeleted(draftSlides, slideIndex, deleted));
  };

  const dropDraftSection = (sectionId: string, deleted: boolean) => {
    if (draftSlides) setDraft(setSectionDeleted(draftSlides, sectionId, deleted));
  };

  const redraftingSectionId = run.job?.regeneratingSectionId ?? null;

  // Handing the draft back to the job lets the redrafted slides through: local edits shadow it.
  const redraftSection = async (sectionId: string) => {
    if (!run.jobId || !draftSlides) return;
    const slides = draftSlides.map(toSlideInput);
    setDraft(null);
    await regenerateSection({ jobId: run.jobId, sectionId, slides });
  };

  const keptSlides = draftSlides?.filter((slide) => !slide.deleted) ?? [];
  const unresolved = keptSlides.filter((slide) => slide.error).length;
  const sectionNames = new Map(
    sections.flatMap((section) => (section.name ? [[section.id, section.name] as const] : []))
  );
  const checks = groupChecks(run.job?.checks ?? [], editedSlideIds);
  const flagged = keptSlides.filter((slide) => slide.id && checks.has(slide.id)).length;

  useEffect(() => {
    if (!run.jobId || draft === null) return;
    const timer = setTimeout(() => {
      void saveDraft({ jobId: run.jobId as string, slides: draft.map(toSlideInput) });
    }, AUTOSAVE_DELAY_MS);
    return () => clearTimeout(timer);
  }, [draft, run.jobId, saveDraft]);

  const addSlides = async () => {
    if (!run.jobId || !draftSlides) return;
    const writtenSlideIds = await confirm({
      jobId: run.jobId,
      slides: draftSlides.map(toSlideInput),
    });
    // Canvas slides never pass through the editor here, so their thumbnails are shot in the
    // background rather than on save.
    enqueueScreenshots(writtenSlideIds);
    onSlidesAdded();
    onClose();
  };

  const usage = run.job?.usage;
  const spend = usage && usage.calls > 0 ? formatTwd(usage.costUsd) : "";
  // A draft with no ledger rows has nothing to show, which must not read as free.
  const spendNote = usage && usage.calls === 0 ? "cost not recorded" : spend;

  const openEditor = (section: GeneratorSection | null) => {
    setEditing(section);
    setIsEditorOpen(true);
  };

  return (
    <>
      <Modal isOpen={isOpen} onClose={onClose} title="Generate Slides" size="xl">
        <div className="space-y-7">
          {draftSlides ? (
            <DraftSheet
              slides={draftSlides}
              sectionNames={sectionNames}
              checks={checks}
              sectionCosts={sectionCosts}
              redraftingSectionId={redraftingSectionId}
              onFieldsChange={editDraftFields}
              onDeletedChange={dropDraftSlide}
              onSectionDeletedChange={dropDraftSection}
              onRedraft={redraftSection}
            />
          ) : run.isRunning ? (
            <div className="flex flex-col items-center gap-3 py-16">
              <LoadingSpinner />
              <p className="body-sm text-gray-500 dark:text-gray-400">
                {run.job?.stage === "checking"
                  ? "Checking the slides for wrong answers and AI-sounding wording…"
                  : `Drafting ${run.job?.progress.total ?? 0} slides… ${run.job ? `${run.job.progress.done} of ${run.job.progress.total}` : ""}`}
                {spend !== "" && ` · ${spend}`}
              </p>
            </div>
          ) : (
            <>
              <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-gray-200 bg-background px-4 py-3 dark:border-gray-800">
                <div>
                  <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                    Start from a preset
                  </h3>
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    A saved book lesson and set of sections, ready to tweak.
                  </p>
                </div>
                <PresetBar
                  target={target}
                  sections={sections}
                  autoApply={false}
                  onApply={applyPreset}
                />
              </div>

              {(sections.length === 0 || needsBook) && (
                <Step
                  number={1}
                  title="Book lesson to generate from"
                  hint="The generated slides draw their content from this book lesson's words, grammar points and page lines. Sections writing subslides read their main slide instead, and need no book lesson."
                >
                  <BookLessonPicker idPrefix="target" value={target} onChange={chooseTarget} />
                </Step>
              )}

              <Step
                number={2}
                title="Sections"
                hint="One per kind of slide to add. Each says what to build, from which part of the book, and how many. Drag to set the order the slides are added in."
              >
                {sections.length === 0 ? (
                  <div className="rounded-xl border border-dashed border-gray-300 px-4 py-8 text-center dark:border-gray-700">
                    <p className="body-sm text-gray-500 dark:text-gray-400">
                      No sections yet. Add one for every kind of slide the new lesson needs.
                    </p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    <Sortable
                      items={sections}
                      onReorder={(next) => setSections(next)}
                      handle
                      strategy={verticalListSortingStrategy}
                    >
                      {(section, index, dragHandle) => (
                        <SectionRow
                          section={section}
                          index={index}
                          tags={tags}
                          targetBook={targetBook}
                          dragHandle={dragHandle}
                          onEdit={() => openEditor(section)}
                          onRemove={() =>
                            setSections((prev) => prev.filter((one) => one.id !== section.id))
                          }
                        />
                      )}
                    </Sortable>
                  </div>
                )}

                <div className="mt-3 flex items-center justify-between">
                  <Button
                    variant="ghost"
                    size="sm"
                    leftIcon={<HiPlus className="h-4 w-4" />}
                    onClick={() => openEditor(null)}
                  >
                    Add section
                  </Button>
                  {sections.length > 0 && (
                    <span className="text-xs text-gray-500 dark:text-gray-400">
                      {needsBook && (
                        <>
                          <strong className="font-semibold text-gray-900 dark:text-gray-100">
                            {totalSlides}
                          </strong>{" "}
                          slide{totalSlides === 1 ? "" : "s"} in total
                        </>
                      )}
                      {needsBook && hasSubslideSection && ", plus "}
                      {hasSubslideSection && "subslides on every main slide the rules match"}
                    </span>
                  )}
                </div>
              </Step>
            </>
          )}

          {run.job?.status === "failed" && (
            <p className="rounded-lg bg-error-500/10 px-3 py-2 text-sm text-error-500">
              {describeAiError(run.job.error, "Generation failed")}
            </p>
          )}

          <div className="flex items-center justify-between border-t border-gray-200 pt-4 dark:border-gray-800">
            <span
              className={`text-xs ${!draftSlides && emptySections.length > 0 ? "text-warning-500" : "text-gray-500 dark:text-gray-400"}`}
            >
              {draftSlides
                ? unresolved > 0
                  ? `${unresolved} slide${unresolved === 1 ? "" : "s"} the generator could not write — fix or drop ${unresolved === 1 ? "it" : "them"} before adding.`
                  : flagged > 0
                    ? `The checker flagged ${flagged} slide${flagged === 1 ? "" : "s"} — advice only, so add ${flagged === 1 ? "it" : "them"} as ${flagged === 1 ? "it is" : "they are"} if you disagree.`
                    : "Edit the wording here, then add these slides to the lesson."
                : emptySections.length > 0
                  ? `${emptySections.map((one) => one.name ?? one.referenceSlide.name).join(", ")}: nothing checked, written from the example slide alone.`
                  : "Nothing is written until you add the slides."}
              {draftSlides && spendNote !== "" && (
                <span className="ml-2 font-mono text-gray-400">{spendNote}</span>
              )}
            </span>
            <div className="flex gap-2">
              <Button variant="ghost" onClick={onClose}>
                Cancel
              </Button>
              {draftSlides ? (
                <>
                  <Button
                    variant="ghost"
                    onClick={() => {
                      setDraft(null);
                      run.reset();
                    }}
                  >
                    Back to setup
                  </Button>
                  <Button
                    onClick={addSlides}
                    isLoading={confirming}
                    disabled={keptSlides.length === 0 || redraftingSectionId !== null}
                  >
                    Add {keptSlides.length} slide{keptSlides.length === 1 ? "" : "s"}
                  </Button>
                </>
              ) : (
                <Button disabled={!canGenerate || run.isRunning} onClick={generate}>
                  Generate
                </Button>
              )}
            </div>
          </div>
        </div>
      </Modal>

      {isEditorOpen && (
        <SectionEditorModal
          isOpen={isEditorOpen}
          onClose={() => setIsEditorOpen(false)}
          onSave={upsertSection}
          lessonId={lessonId}
          targetBook={targetBook}
          bookScope={{
            seriesSlug: target.seriesSlug,
            levelId: target.levelId,
            componentId: target.componentId,
          }}
          canEditLines
          {...(editing ? { section: editing } : {})}
        />
      )}
    </>
  );
}
